import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { MapPin, Clock, Loader2, Building2, ChevronRight } from "lucide-react";

const STATUS_META = {
  upcoming:        { label: "Upcoming",         cls: "bg-amber-50 text-amber-800 border-amber-200" },
  under_planning:  { label: "Under Planning",   cls: "bg-sky-50 text-sky-800 border-sky-200" },
  approved:        { label: "Approved",         cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  in_development:  { label: "In Development",   cls: "bg-primary/10 text-primary border-primary/20" },
};

export default function MasterPlan() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [locationQ, setLocationQ] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const qs = new URLSearchParams();
    if (status) qs.set("status", status);
    if (locationQ) qs.set("location", locationQ);
    api.get(`/master-plans?${qs.toString()}`)
      .then((r) => { if (!cancelled) setItems(r.data.items || []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [status, locationQ]);

  const timelines = useMemo(() => Array.from(new Set(items.map(i => i.timeline).filter(Boolean))).sort(), [items]);

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="master-plan-page">
      <Header />
      <main className="flex-1">
        <section className="bg-primary text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 md:py-16">
            <p className="text-accent text-sm font-semibold tracking-wider uppercase mb-3">Development Pipeline</p>
            <h1 className="font-display text-4xl md:text-5xl font-bold flex items-center gap-3" data-testid="master-plan-title">
              <Building2 className="h-9 w-9 md:h-11 md:w-11 shrink-0 text-accent" />
              <span>Master Plans</span>
            </h1>
            <p className="text-white/80 mt-3 text-base max-w-2xl">Approved, under-planning and upcoming township, road and infrastructure plans across Gurugram, Pataudi and nearby.</p>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex flex-wrap gap-3 items-center bg-surface/40 border border-border rounded-xl p-3">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground pl-1">Filter:</span>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 px-3 rounded-md border border-border bg-white text-sm" data-testid="mp-status-filter">
              <option value="">All statuses</option>
              {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <input value={locationQ} onChange={(e) => setLocationQ(e.target.value)} placeholder="Search by location" className="h-10 px-3 rounded-md border border-border bg-white text-sm min-w-[220px]" data-testid="mp-location-filter" />
            {timelines.length > 0 && (
              <span className="text-xs text-muted-foreground ml-auto">{items.length} plan{items.length !== 1 ? "s" : ""}</span>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading master plans…</div>
          ) : items.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-border rounded-xl mt-6">
              <Building2 className="h-10 w-10 mx-auto text-muted-foreground/50" />
              <p className="mt-3 font-display text-lg font-semibold">No master plans yet</p>
              <p className="text-sm text-muted-foreground">Check back soon — the admin will publish new plans here as they are announced.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-5">
              {items.map((mp) => {
                const meta = STATUS_META[mp.status] || STATUS_META.upcoming;
                return (
                  <Link key={mp.id} to={`/master-plan/${mp.id}`} className="group rounded-2xl overflow-hidden border border-border bg-white hover:border-primary/40 hover:shadow-[0_18px_40px_rgba(0,0,0,.08)] transition-all" data-testid={`mp-card-${mp.id}`}>
                    <div className="aspect-[16/10] bg-neutral-100 overflow-hidden relative">
                      <img src={mp.image_url} alt={mp.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <span className={`absolute top-3 left-3 text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border ${meta.cls}`}>{meta.label}</span>
                    </div>
                    <div className="p-5">
                      <h3 className="font-display font-bold text-lg leading-tight line-clamp-2">{mp.title}</h3>
                      <p className="text-sm text-muted-foreground mt-1.5 line-clamp-2">{mp.description}</p>
                      <div className="mt-3 flex flex-wrap gap-3 text-xs text-ink/70">
                        <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-primary" /> {mp.location}</span>
                        {mp.timeline && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-primary" /> {mp.timeline}</span>}
                      </div>
                      <div className="mt-4 text-sm font-semibold text-primary inline-flex items-center gap-1 group-hover:gap-2 transition-all">View details <ChevronRight className="h-4 w-4" /></div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}
